import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Special } from '@/api/endpoints/bars';
import { CompactSpecialCard } from './CompactSpecialCard';

// Parse price string to number for sorting (e.g., "$5.00" -> 5)
function parsePrice(price: string | null): number {
  if (!price) return Infinity;
  const match = price.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : Infinity;
}

interface HappyHourExpanderProps {
  displayTime: string;
  specials: Special[];
  defaultExpanded?: boolean;
}

export function HappyHourExpander({
  displayTime,
  specials,
  defaultExpanded = false,
}: HappyHourExpanderProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Separate specials by type and sort by price
  const drinkSpecials = specials
    .filter((s) => s.type === 'drink')
    .sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
  const foodSpecials = specials
    .filter((s) => s.type === 'food')
    .sort((a, b) => parsePrice(a.price) - parsePrice(b.price));
  const comboSpecials = specials
    .filter((s) => s.type === 'combo')
    .sort((a, b) => parsePrice(a.price) - parsePrice(b.price));

  return (
    <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-[var(--color-card)] hover:bg-[var(--color-border)] transition-colors"
      >
        <span className="font-medium">{displayTime}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {isExpanded && (
        <div className="p-3 space-y-3">
          {drinkSpecials.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--color-muted)] mb-1 uppercase tracking-wide">
                Drinks
              </p>
              <div>
                {drinkSpecials.map((s) => (
                  <CompactSpecialCard key={s.id} special={s} />
                ))}
              </div>
            </div>
          )}

          {foodSpecials.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--color-muted)] mb-1 uppercase tracking-wide">
                Food
              </p>
              <div>
                {foodSpecials.map((s) => (
                  <CompactSpecialCard key={s.id} special={s} />
                ))}
              </div>
            </div>
          )}

          {comboSpecials.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--color-muted)] mb-1 uppercase tracking-wide">
                Combos
              </p>
              <div>
                {comboSpecials.map((s) => (
                  <CompactSpecialCard key={s.id} special={s} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
